import React, { useEffect, useState } from "react"
import { Table, Button, Drawer, Form, Input, Popconfirm, message } from "antd"
import { useHistory } from "react-router-dom"
import { EditOutlined, DeleteOutlined, PlusOutlined, EyeOutlined } from "@ant-design/icons"
import { CarOutlined, DollarOutlined, BarChartOutlined } from "@ant-design/icons"
import { ArrowUp, ArrowDown, Euro } from "lucide-react"
import { secureApi } from "../../Config/api"
import { GET, POST } from "../../utils/apis"
import allPaths from '../../Config/paths'
import { useDispatch, useSelector } from "react-redux"
import { translations } from "../../utils/constants"
import { verifyToken } from "../../utils/helpers"
import paths from "../../Config/paths"

const EstimationSystem = ({ language }) => {
    const history = useHistory()
    const dispatch = useDispatch()
    const user = useSelector((state) => state.authReducer?.user)
    const [vehicles, setVehicles] = useState([])
    const [loading, setLoading] = useState(false)
    const [drawerVisible, setDrawerVisible] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [currentVehicle, setCurrentVehicle] = useState(null)
    const [saveLoading, setSaveLoading] = useState(false)
    const [form] = Form.useForm()
    const [grandTotal, setGrandTotal] = useState(0)
    const isUser = user?.role === "user"

    useEffect(() => {
        verifyToken(history, dispatch)
    }, [])

    useEffect(() => {
        if (user && user.role === 'teamTrack') {
            history.push(paths?.HOME)
        }
    }, [user, history])


    const fetchVehicles = async () => {
        try {
            setLoading(true)
            const res = await secureApi?.get(`${GET?.GET_VEHICLES}?user_id=${user?.id}`)
            if (res?.success) {
                setVehicles(res.vehicles || [])
            } else {
                // message.error(res?.message || translations[language]?.fetchVehiclesError)
            }
        } catch (err) {
            message.error(translations[language]?.fetchVehiclesError)
        } finally {
            setLoading(false)
        }
    }

    const fetchEstimationTotal = async () => {
        try {
            const res = await secureApi?.get(`${GET?.GET_ESTIMATION_TOTAL}?user_id=${user?.id}`)
            // console.log(res, 'res')
            if (res?.success) {
                setGrandTotal(res.data?.grandTotal || 0)
            } else {
                // message.error(res?.message || "Failed to fetch estimation totals")
            }
        } catch (err) {
            message.error("Failed to fetch estimation totals")
        }
    }

    useEffect(() => {
        if (user?.id) {
            fetchVehicles()
            fetchEstimationTotal()
        }
    }, [user])

    const handleAdd = () => {
        setIsEditMode(false)
        form.resetFields()
        setDrawerVisible(true)
    }

    const handleEdit = (record) => {
        setIsEditMode(true)
        setCurrentVehicle(record)
        form.setFieldsValue({
            vehicle_name: record.name,
            license_number: record.license_number,
            registering_number: record.registering_number,
        })
        setDrawerVisible(true)
    }

    const handleDelete = async (id) => {
        try {
            const res = await secureApi.post(POST.DELETE_VEHICLE, {
                id,
                user_id: user.id,
            })
            if (res?.success) {
                message.success(translations[language]?.vehicleDeleted)
                fetchVehicles()
                fetchEstimationTotal()
            } else {
                message.error(res?.message || translations[language]?.vehicleDeleteError)
            }
        } catch (err) {
            message.error(translations[language]?.vehicleDeleteError)
        }
    }

    const handleSave = async () => {
        try {
            const values = await form.validateFields()
            setSaveLoading(true)
            if (isEditMode) {
                const res = await secureApi.post(POST.EDIT_VEHICLE, {
                    id: currentVehicle.id,
                    user_id: user.id,
                    ...values,
                })
                if (res?.success) {
                    message.success(translations[language]?.vehicleUpdated)
                    fetchVehicles()
                    fetchEstimationTotal()
                    setDrawerVisible(false)
                } else {
                    message.error(res?.message || translations[language]?.vehicleUpdateError)
                }
            } else {
                const res = await secureApi.post(POST.ADD_VEHICLE, {
                    user_id: user.id,
                    ...values,
                })
                if (res?.success) {
                    message.success(translations[language]?.vehicleAdded)
                    fetchVehicles()
                    fetchEstimationTotal()
                    setDrawerVisible(false)
                } else {
                    message.error(res?.message || translations[language]?.vehicleAddError)
                }
            }
        } catch (err) {
            message.error(translations[language]?.vehicleSaveError)
        }
        finally {
            setSaveLoading(false)
        }
    }

    const columns = [
        {
            title: translations[language]?.vehicleName,
            dataIndex: "name",
            key: "name",
            render: (text, record) => (
                <div
                    onClick={() =>
                        history.push(`${allPaths?.VEHICLE_DETAILS}/${record.id}`, { vehicle: record })
                    }
                    style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
                >
                    <EyeOutlined style={{ color: "red" }} />
                    <span>{text}</span>
                </div>
            ),
        },
        {
            title: translations[language]?.licenseNumber,
            dataIndex: "license_number",
            key: "license_number",
        },
        {
            title: translations[language]?.registeringNumber,
            dataIndex: "registering_number",
            key: "registering_number",
        },
        {
            title: translations[language]?.TotalCost,
            dataIndex: "total",
            key: "total",
            render: (value) => `€ ${Number(value || 0).toFixed(2)}`
        },
        {
            title: translations[language]?.actions,
            key: "actions",
            render: (_, record) => (
                <div style={{ display: "flex", gap: "10px" }}>
                    <EditOutlined
                        style={{
                            color: isUser ? "#ccc" : "#1890ff",
                            cursor: isUser ? "not-allowed" : "pointer"
                        }}
                        onClick={() => !isUser && handleEdit(record)}
                    />
                    <Popconfirm
                        title={translations[language]?.confirmDeleteVehicle}
                        onConfirm={() => !isUser && handleDelete(record.id)}
                        okText={translations[language]?.yes}
                        cancelText={translations[language]?.no}
                        disabled={isUser}
                    >
                        <DeleteOutlined
                            style={{
                                color: isUser ? "#ccc" : "red",
                                cursor: isUser ? "not-allowed" : "pointer"
                            }}
                        />
                    </Popconfirm>
                </div>
            ),
        },
    ].filter(col => !(isUser && col.key === "total"))

    const TrendIndicator = ({ trend, isPositive }) => (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                color: isPositive ? "#28a745" : "#dc3545",
                fontWeight: 500,
            }}
        >
            {isPositive ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
            <span>{Math.abs(trend)}%</span>
        </div>
    )

    const dashboardCards = [
        {
            title: translations[language]?.totalVehicles,
            value: vehicles.length,
            icon: (
                <div
                    style={{
                        background: "#e6f4ff",
                        borderRadius: 8,
                        padding: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CarOutlined style={{ fontSize: 22, color: "#1677ff" }} />
                </div>
            ),
            trend: { value: 8.5, isPositive: true },
            period: translations[language]?.thisMonth,
        },
        {
            title: translations[language]?.TotalCost,
            value: `€ ${grandTotal.toFixed(2)}`,
            icon: (
                <div
                    style={{
                        background: "#f6ffed",
                        borderRadius: 8,
                        padding: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Euro style={{ fontSize: 22, color: "#52c41a" }} />
                </div>
            ),
            trend: { value: -3.2, isPositive: false },
            period: translations[language]?.thisMonth,
        }
    ]

    return (
        <div className="home-container" style={{ padding: 24 }}>
            <div
                className="home-header"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "1rem",
                }}
            >
                <div>
                    <h1 className="timesheet-title">{translations[language]?.vehiclesDashboard}</h1>
                    <p className="timesheet-subtitle">
                        {translations[language]?.vehiclesSubtitle}
                    </p>
                </div>
                {!isUser && (
                    <Button
                        type="primary"
                        className="export-btn"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        {translations[language]?.addVehicle}
                    </Button>
                )}
            </div>

            {!isUser && (
                <div style={{ marginTop: 32 }}>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: 16,
                        }}
                    >
                        {dashboardCards?.map((card, index) => (
                            <div key={index} className="dashboard-card">
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 8,
                                    }}
                                >
                                    {card.icon}
                                    <TrendIndicator
                                        trend={card.trend.value}
                                        isPositive={card.trend.isPositive}
                                    />
                                </div>
                                <h3 style={{ margin: "8px 0", fontSize: "15px" }}>{card.title}</h3>
                                <p style={{ fontSize: 18, fontWeight: 600 }}>{card.value}</p>
                                <div style={{ marginTop: 8 }}>
                                    <span style={{ fontSize: 12, color: "#999" }}>{card.period}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div style={{ marginTop: 40 }}>
                <div>
                    <h1 style={{ textAlign: "start" }}>{translations[language]?.vehicles}</h1>
                </div>
                <Table
                    columns={columns}
                    dataSource={vehicles}
                    rowKey="id"
                    bordered
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    className="custom-timesheet-table"
                />
            </div>

            <Drawer
                title={isEditMode ? translations[language]?.editVehicle : translations[language]?.addVehicle}
                width={720}
                onClose={() => setDrawerVisible(false)}
                open={drawerVisible}
                destroyOnClose
                placement="right"
                footer={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 12,
                            padding: "10px 16px"
                        }}
                    >
                        <Button onClick={() => setDrawerVisible(false)} className="cancel-button">
                            {translations[language]?.cancel}
                        </Button>
                        <Button type="primary" onClick={handleSave} loading={saveLoading} className="submit-button">
                            {isEditMode ? translations[language]?.update : translations[language]?.saveButton}
                        </Button>
                    </div>
                }
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="vehicle_name"
                        label={translations[language]?.vehicleName}
                        rules={[{ required: true, message: translations[language]?.vehicleNameReq }]}
                    >
                        <Input placeholder={translations[language]?.enterVehicleName} className="antd-input" />
                    </Form.Item>
                    <Form.Item
                        name="license_number"
                        label={translations[language]?.licenseNumber}
                        rules={[{ required: true, message: translations[language]?.licenseNumberReq }]}
                    >
                        <Input placeholder={translations[language]?.enterLicenseNumber}
                            className="antd-input" />
                    </Form.Item>
                    <Form.Item
                        name="registering_number"
                        label={translations[language]?.registeringNumber}
                        rules={[{ required: true, message: translations[language]?.registeringNumberReq }]}
                    >
                        <Input placeholder={translations[language]?.enterRegisteringNumber} className="antd-input" />
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    )
}

export default EstimationSystem
